import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import {
  ChevronLeft,
  ChevronRight,
  ChevronRight as ChevronRightSmall,
  Package,
  Sparkles,
  Star,
  Tag,
  TrendingUp,
} from 'lucide-react';
import { BrandBannerItem } from './BrandResponsiveBanner';
import { getFullImageUrl } from '../../utils/imageUtils';

type BrandInfo = {
  name: string;
  slug?: string;
  logo?: string;
  description?: string;
};

type Props = {
  brand: BrandInfo;
  banners: BrandBannerItem[];
  productCount: number;
  avgDiscount: number;
  avgRating: string | number;
};

const SLIDE_MS = 5500;

const BrandIntegratedHero: React.FC<Props> = ({
  brand,
  banners,
  productCount,
  avgDiscount,
  avgRating,
}) => {
  const slides = useMemo(
    () => banners.filter((b) => b.image).sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    [banners]
  );
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  const go = useCallback(
    (dir: -1 | 1) => {
      if (slides.length <= 1) return;
      setIndex((i) => (i + dir + slides.length) % slides.length);
    },
    [slides.length]
  );

  useEffect(() => {
    if (slides.length <= 1 || paused) return;
    const t = window.setInterval(() => go(1), SLIDE_MS);
    return () => window.clearInterval(t);
  }, [slides.length, paused, go]);

  useEffect(() => {
    if (index >= slides.length) setIndex(0);
  }, [index, slides.length]);

  const brandLogo = brand.logo
    ? getFullImageUrl(brand.logo)
    : `https://ui-avatars.com/api/?name=${encodeURIComponent(brand.name)}&size=200&background=0f172a&color=f8fafc&font-size=0.5&bold=true`;

  const active = slides[index];

  const renderSlideImage = (
    banner: BrandBannerItem,
    isActive: boolean,
    fit: 'contain' | 'cover'
  ) => {
    const img = (
      <img
        src={getFullImageUrl(banner.image)}
        alt={banner.alt || `${brand.name} banner`}
        className={`absolute inset-0 h-full w-full ${
          fit === 'contain' ? 'object-contain object-center' : 'object-cover object-center'
        }`}
        loading={isActive ? 'eager' : 'lazy'}
        decoding="async"
      />
    );

    const linked =
      banner.link?.startsWith('/') ? (
        <Link to={banner.link} className="block h-full w-full" tabIndex={isActive ? 0 : -1}>
          {img}
        </Link>
      ) : banner.link?.startsWith('http') ? (
        <a href={banner.link} target="_blank" rel="noopener noreferrer" className="block h-full w-full">
          {img}
        </a>
      ) : (
        img
      );

    if (fit === 'contain') {
      return (
        <div className="absolute inset-0 bg-slate-950">
          {linked}
          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-8 bg-gradient-to-t from-slate-950/80 to-transparent" />
        </div>
      );
    }

    return (
      <div className="absolute inset-0">
        {linked}
        <div className="absolute inset-0 bg-slate-900/55" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-900/40 to-slate-900/30" />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950/80 via-slate-900/35 to-transparent" />
      </div>
    );
  };

  const slideCarousel = (fit: 'contain' | 'cover', className: string) => (
    <motion.div className={className}>
      {slides.length > 0 ? (
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={active?._id || active?.image || index}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.45, ease: 'easeOut' }}
            className="absolute inset-0"
          >
            {active && renderSlideImage(active, true, fit)}
          </motion.div>
        </AnimatePresence>
      ) : (
        <motion.div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900" />
      )}
    </motion.div>
  );

  const navBtnClass =
    'rounded-full border border-white/20 bg-black/40 p-2 text-white backdrop-blur-md transition-colors hover:bg-black/55';

  const renderCarouselControls = (variant: 'mobile' | 'desktop') => {
    if (slides.length <= 1) return null;

    if (variant === 'mobile') {
      return (
        <>
          <button
            type="button"
            onClick={() => go(-1)}
            className={`absolute left-2 top-1/2 z-30 -translate-y-1/2 ${navBtnClass}`}
            aria-label="Previous banner"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => go(1)}
            className={`absolute right-2 top-1/2 z-30 -translate-y-1/2 ${navBtnClass}`}
            aria-label="Next banner"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
          <motion.div className="absolute bottom-2 left-1/2 z-30 flex -translate-x-1/2 gap-1.5">
            {slides.map((s, i) => (
              <button
                key={s._id || s.image || i}
                type="button"
                onClick={() => setIndex(i)}
                className={`h-1.5 rounded-full transition-all ${
                  i === index ? 'w-6 bg-white' : 'w-1.5 bg-white/45 hover:bg-white/70'
                }`}
                aria-label={`Banner ${i + 1}`}
              />
            ))}
          </motion.div>
        </>
      );
    }

    return (
      <>
        <button
          type="button"
          onClick={() => go(-1)}
          className={`absolute left-6 top-1/2 z-30 hidden -translate-y-1/2 md:block ${navBtnClass} p-2.5`}
          aria-label="Previous banner"
        >
          <ChevronLeft className="h-5 w-5" />
        </button>
        <button
          type="button"
          onClick={() => go(1)}
          className={`absolute right-6 top-1/2 z-30 hidden -translate-y-1/2 md:block ${navBtnClass} p-2.5`}
          aria-label="Next banner"
        >
          <ChevronRight className="h-5 w-5" />
        </button>
        <motion.div className="absolute bottom-[30%] left-1/2 z-30 hidden -translate-x-1/2 gap-2 md:flex">
          {slides.map((s, i) => (
            <button
              key={s._id || s.image || i}
              type="button"
              onClick={() => setIndex(i)}
              className={`h-1.5 rounded-full transition-all ${
                i === index ? 'w-8 bg-white' : 'w-2 bg-white/40 hover:bg-white/60'
              }`}
              aria-label={`Banner ${i + 1}`}
            />
          ))}
        </motion.div>
      </>
    );
  };

  return (
    <section
      className="relative overflow-hidden bg-slate-950 md:min-h-[520px] lg:min-h-[560px]"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {/* Mobile: show full banner (object-contain), brand block below */}
      {slides.length > 0 && (
        <motion.div className="relative md:hidden">
          {slideCarousel(
            'contain',
            'relative aspect-[16/9] w-full min-h-[180px] max-h-[min(52vh,360px)] sm:aspect-[2/1] sm:max-h-[min(48vh,400px)]'
          )}
          {renderCarouselControls('mobile')}
        </motion.div>
      )}

      {/* Desktop: banner as background behind brand info */}
      <motion.div className="absolute inset-0 hidden md:block">
        {slideCarousel('cover', 'absolute inset-0')}
        {slides.length === 0 && (
          <>
            <motion.div
              animate={{ x: [0, 40, 0], y: [0, -15, 0] }}
              transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut' }}
              className="absolute -top-24 -left-24 h-[420px] w-[420px] rounded-full bg-blue-600/20 blur-3xl"
            />
            <motion.div
              animate={{ x: [0, -25, 0], y: [0, 20, 0] }}
              transition={{ duration: 14, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
              className="absolute -bottom-24 -right-24 h-[480px] w-[480px] rounded-full bg-emerald-500/15 blur-3xl"
            />
          </>
        )}
      </motion.div>

      {renderCarouselControls('desktop')}

      <motion.div className="relative z-20 flex flex-col md:min-h-[520px] lg:min-h-[560px]">
        <motion.div className="container mx-auto flex flex-1 flex-col px-4 sm:px-6 lg:px-8">
          <nav
            className={`flex items-center gap-2 text-sm text-white/55 ${
              slides.length > 0 ? 'py-4 md:pt-8' : 'pt-6 md:pt-8'
            }`}
            aria-label="Breadcrumb"
          >
            <Link to="/" className="transition-colors hover:text-white">
              Home
            </Link>
            <ChevronRightSmall className="h-3.5 w-3.5 shrink-0" />
            <Link to="/brands" className="transition-colors hover:text-white">
              Brands
            </Link>
            <ChevronRightSmall className="h-3.5 w-3.5 shrink-0" />
            <span className="font-medium text-white">{brand.name}</span>
          </nav>

          <motion.div className="hidden flex-1 md:block" />

          <motion.div className="pb-8 pt-2 md:pb-12 md:pt-8">
            <motion.div className="flex flex-col gap-5 md:flex-row md:items-end md:gap-8">
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.45 }}
                className="relative shrink-0"
              >
                <motion.div className="absolute -inset-2 rounded-3xl bg-gradient-to-r from-blue-500/40 to-cyan-500/30 blur-xl" />
                <motion.div className="relative flex h-20 w-20 items-center justify-center overflow-hidden rounded-2xl border border-white/25 bg-white/10 shadow-2xl backdrop-blur-md sm:h-24 sm:w-24 md:h-28 md:w-28">
                  <img
                    src={brandLogo}
                    alt={brand.name}
                    className="h-full w-full object-contain p-2.5 sm:p-3"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = `https://ui-avatars.com/api/?name=${encodeURIComponent(brand.name)}&size=200&background=0f172a&color=f8fafc`;
                    }}
                  />
                </motion.div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.08 }}
                className="min-w-0 flex-1"
              >
                <motion.div className="mb-2 flex flex-wrap items-center gap-2">
                  <span className="inline-flex items-center gap-1.5 rounded-full border border-white/20 bg-white/10 px-3 py-1 text-xs text-white/90 backdrop-blur-md">
                    <Sparkles className="h-3 w-3 text-amber-400" />
                    Official Store
                  </span>
                  {avgDiscount > 0 && (
                    <span className="inline-flex items-center gap-1 rounded-full border border-red-400/30 bg-red-500/20 px-3 py-1 text-xs text-red-200 backdrop-blur-md">
                      <Tag className="h-3 w-3" />
                      Up to {avgDiscount}% off
                    </span>
                  )}
                </motion.div>

                <h1 className="mb-2 text-2xl font-extrabold tracking-tight text-white drop-shadow-sm sm:text-3xl md:text-4xl lg:text-5xl">
                  {brand.name}
                </h1>
                {brand.description && (
                  <p className="mb-4 line-clamp-2 max-w-2xl text-sm text-white/65 md:text-base">{brand.description}</p>
                )}

                <motion.div className="flex flex-wrap gap-2.5 sm:gap-3">
                  <motion.div className="flex items-center gap-2.5 rounded-xl border border-white/10 bg-white/10 px-3 py-2 backdrop-blur-md sm:px-4 sm:py-2.5">
                    <motion.div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-blue-500 to-blue-600">
                      <Package className="h-4 w-4 text-white" />
                    </motion.div>
                    <motion.div>
                      <motion.div className="text-sm font-bold text-white">{productCount}</motion.div>
                      <motion.div className="text-[10px] uppercase tracking-wider text-white/45">Products</motion.div>
                    </motion.div>
                  </motion.div>
                  {Number(avgRating) > 0 && (
                    <motion.div className="flex items-center gap-2.5 rounded-xl border border-white/10 bg-white/10 px-3 py-2 backdrop-blur-md sm:px-4 sm:py-2.5">
                      <motion.div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-amber-500 to-orange-500">
                        <Star className="h-4 w-4 fill-white text-white" />
                      </motion.div>
                      <motion.div>
                        <motion.div className="text-sm font-bold text-white">{avgRating}</motion.div>
                        <motion.div className="text-[10px] uppercase tracking-wider text-white/45">Avg Rating</motion.div>
                      </motion.div>
                    </motion.div>
                  )}
                  <motion.div className="flex items-center gap-2.5 rounded-xl border border-white/10 bg-white/10 px-3 py-2 backdrop-blur-md sm:px-4 sm:py-2.5">
                    <motion.div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-emerald-500 to-emerald-600">
                      <TrendingUp className="h-4 w-4 text-white" />
                    </motion.div>
                    <motion.div>
                      <motion.div className="text-sm font-bold text-white">100%</motion.div>
                      <motion.div className="text-[10px] uppercase tracking-wider text-white/45">Authentic</motion.div>
                    </motion.div>
                  </motion.div>
                </motion.div>
              </motion.div>
            </motion.div>
          </motion.div>
        </motion.div>
      </motion.div>

      <motion.div className="pointer-events-none absolute bottom-0 left-0 right-0 z-10 hidden h-20 bg-gradient-to-t from-gray-50 to-transparent md:block" />
    </section>
  );
};

export default BrandIntegratedHero;
